import { createContext, useContext, useEffect, useRef, ReactNode } from 'react';
import { useOrganizationBranding } from '@/hooks/use-branding';
import { useAuth } from '@/contexts/auth-context';
import type { OrganizationBranding } from '@/lib/types';

const DEFAULT_PRIMARY = '#0060FF';
const DEFAULT_SECONDARY = '#666666';

interface BrandingContextType {
  branding: OrganizationBranding | null | undefined;
  isLoading: boolean;
}

const BrandingContext = createContext<BrandingContextType>({
  branding: null,
  isLoading: false,
});

/**
 * Compute relative luminance for a hex color.
 * Returns a value between 0 (darkest) and 1 (lightest).
 */
function getLuminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function applyBrandingTheme(branding: OrganizationBranding | null | undefined) {
  const root = document.documentElement;

  if (!branding) {
    // Reset to defaults
    root.style.setProperty('--primary', DEFAULT_PRIMARY);
    root.style.setProperty('--sidebar-primary', DEFAULT_PRIMARY);
    root.style.setProperty('--secondary', DEFAULT_SECONDARY);
    root.style.removeProperty('--primary-foreground');
    return;
  }

  const primaryColor = branding.primary_color || DEFAULT_PRIMARY;
  const secondaryColor = branding.secondary_color || DEFAULT_SECONDARY;

  root.style.setProperty('--primary', primaryColor);
  root.style.setProperty('--sidebar-primary', primaryColor);

  if (branding.secondary_color) {
    root.style.setProperty('--secondary', secondaryColor);
  }

  // Contrast check: if primary color is too light, use dark foreground text
  if (branding.primary_color) {
    const luminance = getLuminance(primaryColor);
    if (luminance > 0.5) {
      root.style.setProperty('--primary-foreground', '#1a1a2e');
    } else {
      root.style.removeProperty('--primary-foreground');
    }
  }
}

function resetBrandingTheme() {
  const root = document.documentElement;
  root.style.setProperty('--primary', DEFAULT_PRIMARY);
  root.style.setProperty('--sidebar-primary', DEFAULT_PRIMARY);
  root.style.setProperty('--secondary', DEFAULT_SECONDARY);
  root.style.removeProperty('--primary-foreground');
}

interface BrandingProviderProps {
  children: ReactNode;
}

export function BrandingProvider({ children }: BrandingProviderProps) {
  const { isAuthenticated, user } = useAuth();
  const { data: branding, isLoading } = useOrganizationBranding();
  const lastOrgRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      resetBrandingTheme();
      lastOrgRef.current = null;
      return;
    }

    const currentOrg = user?.organization ?? null;
    const previousOrg = lastOrgRef.current;

    // Only reset to defaults when the active organization actually changes,
    // not on every loading state, so the current tenant's theme does not
    // flash during refetches.
    if (previousOrg !== null && currentOrg !== previousOrg) {
      resetBrandingTheme();
    }

    if (branding) {
      applyBrandingTheme(branding);
    }

    lastOrgRef.current = currentOrg;
  }, [branding, isAuthenticated, user?.organization]);

  // Reset on unmount (logout)
  useEffect(() => {
    return () => {
      resetBrandingTheme();
    };
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, isLoading }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding(): BrandingContextType {
  return useContext(BrandingContext);
}
